/*
 * ND Welcome Popup
 * ------------------------------------------------------------
 * Shows a reassurance message to visitors arriving from a
 * decommissioned store that now redirects here. The source
 * site is detected from a configurable URL query parameter
 * (e.g. ?from=telecomscentre.co.uk) and/or document.referrer,
 * then matched against merchant-defined blocks. Source domains
 * are NOT hardcoded here — they come from the section settings.
 */
(function () {
  var STORAGE_SOURCE_KEY = 'nd-welcome-popup:source';
  var STORAGE_DISMISS_PREFIX = 'nd-welcome-popup:dismissed:';

  // Strip protocol, leading www. and surrounding whitespace; lowercase.
  function normalize(value) {
    if (!value) return '';
    return String(value)
      .toLowerCase()
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '');
  }

  function safeStorage(type) {
    try {
      var store = type === 'local' ? window.localStorage : window.sessionStorage;
      var probe = '__nd_probe__';
      store.setItem(probe, '1');
      store.removeItem(probe);
      return store;
    } catch (e) {
      return null;
    }
  }

  var sessionStore = safeStorage('session');
  var localStore = safeStorage('local');

  function readSession(key) {
    return sessionStore ? sessionStore.getItem(key) : null;
  }
  function writeSession(key, value) {
    if (sessionStore) sessionStore.setItem(key, value);
  }

  // Build the string we search matches against, most-to-least reliable:
  //  1. the current hostname — set when an old domain is kept as an alias
  //     that still serves this store (e.g. the theme loads on
  //     telecomscentre.co.uk). This is the dependable signal.
  //  2. a query parameter (?from=...) — only possible if the redirect is
  //     done at the edge (Cloudflare/registrar), not a plain Shopify 301.
  //  3. document.referrer — best-effort fallback; usually lost on a 301.
  // The result is persisted for the session because 1/2 only exist on the
  // landing page, not on subsequent internal navigation.
  function resolveSource(paramName) {
    var host = normalize(window.location.hostname);

    var params = new URLSearchParams(window.location.search);
    var fromParam = normalize(params.get(paramName));

    var referrer = '';
    if (document.referrer) {
      try {
        var url = new URL(document.referrer);
        // Ignore self-referrals (internal navigation).
        if (url.host !== window.location.host) {
          referrer = normalize(url.host + url.pathname);
        }
      } catch (e) {
        referrer = normalize(document.referrer);
      }
    }

    var stored = readSession(STORAGE_SOURCE_KEY) || '';

    // Dedupe: the stored value is re-read and re-written on every page load,
    // so without this the string grows on each internal navigation.
    var seen = {};
    var haystack = [host, fromParam, referrer]
      .concat(stored.split(' '))
      .filter(function (part) {
        if (!part || seen[part]) return false;
        seen[part] = true;
        return true;
      })
      .join(' ')
      .trim();

    if (haystack) writeSession(STORAGE_SOURCE_KEY, haystack);
    return haystack;
  }

  function matchBlock(messages, source) {
    for (var i = 0; i < messages.length; i++) {
      var tokens = (messages[i].match || '')
        .split(',')
        .map(normalize)
        .filter(Boolean);
      for (var j = 0; j < tokens.length; j++) {
        if (source.indexOf(tokens[j]) !== -1) return messages[i];
      }
    }
    return null;
  }

  // Keyed on the block's Shopify id, not its position, so reordering
  // messages in the theme editor doesn't invalidate stored dismissals.
  function dismissKey(key) {
    return STORAGE_DISMISS_PREFIX + key;
  }

  function isSeen(config, key) {
    if (config.frequency === 'always') return false;
    var store = config.frequency === 'once' ? localStore : sessionStore;
    return !!(store && store.getItem(dismissKey(key)));
  }

  function markSeen(config, key) {
    if (config.frequency === 'always') return;
    var store = config.frequency === 'once' ? localStore : sessionStore;
    if (store) store.setItem(dismissKey(key), '1');
  }

  function init(root) {
    var configEl = root.querySelector('[data-welcome-popup-config]');
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    var dialog = root.querySelector('[data-welcome-popup-dialog]');
    var overlay = root.querySelector('[data-welcome-popup-overlay]');
    if (!dialog) return;

    var previewId = root.getAttribute('data-preview-block');
    var targetId;
    var targetKey;

    if (previewId) {
      // Theme editor: always show the selected/first block for styling.
      targetId = previewId;
    } else {
      if (!config.enabled || !config.messages || !config.messages.length) return;
      var source = resolveSource(config.param);
      if (!source) return;
      var match = matchBlock(config.messages, source);
      if (!match || isSeen(config, match.key)) return;
      targetId = match.id;
      targetKey = match.key;
    }

    var message = root.querySelector('[data-welcome-message="' + targetId + '"]');
    if (!message) return;

    var lastFocused = document.activeElement;

    function open() {
      message.hidden = false;
      root.hidden = false;
      root.classList.add('nd-welcome-popup--active');
      document.addEventListener('keydown', onKeydown);
      // Record as seen on display, not on close. The call-to-action is a
      // plain link, so a visitor who clicks it navigates away without ever
      // running close() — and would otherwise be shown the popup again on
      // the destination page, and on every page after that.
      if (!previewId) markSeen(config, targetKey);
      var focusable = dialog.querySelector('[data-welcome-popup-close]') || dialog;
      if (focusable && focusable.focus) focusable.focus();
    }

    function close() {
      root.classList.remove('nd-welcome-popup--active');
      root.hidden = true;
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function onKeydown(event) {
      if (event.key === 'Escape') close();
    }

    root.querySelectorAll('[data-welcome-popup-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    if (overlay) overlay.addEventListener('click', close);

    if (previewId) {
      open();
    } else {
      var delay = parseInt(config.delay, 10) || 0;
      window.setTimeout(open, delay);
    }
  }

  function boot() {
    document.querySelectorAll('[data-welcome-popup]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-welcome-popup]');
    if (root) init(root);
  });
})();
